import { useEffect, useState } from "react";
import { clientEnv } from "lib/env";
import { initializeDatadogRum } from "../observability/datadogRum";
import { safeTrack } from "../observability/tracking/safeTrack";

interface UseFacebookSdkOptions {
  appId?: string | null;
  sdkVersion?: string | null;
  timeoutMs?: number;
  autoload?: boolean; // if false, user must call load() manually
}

export interface FacebookSdkState {
  ready: boolean;
  loading: boolean;
  error: string | null;
}

const SCRIPT_ID = "facebook-jssdk";

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("No window"));
      return;
    }

    // Already present?
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Facebook SDK script"));

    document.head.appendChild(script);
  });
}

export function useFacebookSdk({
  appId = clientEnv.NEXT_PUBLIC_FACEBOOK_APP_ID,
  sdkVersion = clientEnv.NEXT_PUBLIC_FACEBOOK_SDK_VERSION,
  timeoutMs = 20000,
  autoload = true,
}: UseFacebookSdkOptions = {}): FacebookSdkState {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!autoload) return;

    let aborted = false;
    let localTimeout: number | null = null;
    let localAnimFrame: number | null = null;

    const initSdk = async () => {
      if (!appId) {
        setError("Facebook App ID is missing");
        return;
      }
      setLoading(true);
      setError(null);

      try {
        try {
          await initializeDatadogRum();
        } catch {
          // Silently ignore Datadog initialization errors - don't block SDK loading
        }

        if (aborted) return;

        await injectScript();

        if (aborted) return;

        localTimeout = window.setTimeout(() => {
          if (!aborted) {
            setError("Facebook SDK loading timed out");
            setLoading(false);
          }
        }, timeoutMs);

        const start = Date.now();
        const poll = () => {
          localAnimFrame = null;
          if (aborted) return;
          if (window.FB) {
            try {
              window.FB.init({
                appId,
                cookie: true,
                xfbml: true,
                version: sdkVersion,
              });
              if (!aborted) {
                setReady(true);
                setLoading(false);
                setError(null);
              }
              if (localTimeout !== null) {
                window.clearTimeout(localTimeout);
                localTimeout = null;
              }
              safeTrack("sdk_initialized", {
                appId,
                elapsed_ms: Date.now() - start,
              });
            } catch (e) {
              if (localTimeout !== null) {
                window.clearTimeout(localTimeout);
                localTimeout = null;
              }
              if (!aborted) {
                setError("Failed to initialize Facebook SDK");
                setLoading(false);
              }
            }
            return;
          }
          if (Date.now() - start < timeoutMs) {
            localAnimFrame = window.requestAnimationFrame(poll);
          }
        };
        poll();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Unknown SDK load error";
        if (!aborted) {
          setError(msg);
          setLoading(false);
        }
      }
    };

    initSdk();

    return () => {
      aborted = true;
      if (localTimeout !== null) {
        window.clearTimeout(localTimeout);
      }
      if (localAnimFrame !== null) {
        window.cancelAnimationFrame(localAnimFrame);
      }
    };
  }, [autoload, appId, sdkVersion, timeoutMs]);

  return {
    ready,
    loading,
    error,
  };
}
